import type { RevisionFileMatch } from '../../../../../hooks/api/revisions/useFileMatchesForRevision';

export type RevisionFileMatchForTree = Omit<RevisionFileMatch, 'id'>;

type FileNodeComponent = {
  type: 'file';
  name: string;
  path: string;
  match: RevisionFileMatchForTree;
};

type DirectoryNodeComponent = {
  type: 'directory';
  name: string;
  path: string;
  children: MatchTreeNode[];
};

export type MatchTreeNode = FileNodeComponent | DirectoryNodeComponent;

/**
 * Splits a path into segments, filtering any empty/bad nodes
 * @param {string} path - A full file path.
 */
const splitPathIntoSegments = (path: string): string[] => path
  .split('/')
  .filter(segment => segment?.trim());

/**
 * Insert a path in the tree.
 * Doesn't return anything because it is setting the various children Maps
 * @param {MatchTreeNode} root - The root node to insert children into.
 * @param {RevisionFileMatch} match - The match to insert.
 */
function insertPath(root: MatchTreeNode, match: RevisionFileMatchForTree): void {
  let currentNode = root;

  //  Iterate each segment, injecting the children at the correct node
  const segments = splitPathIntoSegments(match.path);
  let currentPath = '';

  segments.forEach((segment, idx) => {
    if (currentNode.type === 'directory') {
      // Build the full path up to this point
      currentPath = currentPath ? `${currentPath}/${segment}` : `/${segment}`;

      let currentSegment = currentNode.children.find(child => child.path === currentPath);

      //  If the current node doesn't have the segment, set it up
      if (!currentSegment) {
        if (idx < segments.length - 1) {
          // This is a directory
          currentSegment = {
            type: 'directory',
            name: segment,
            path: currentPath,
            children: [],
          };
          currentNode.children.push(currentSegment);
        } else {
          // This is the final segment (a file)
          currentSegment = {
            type: 'file',
            name: segment,
            path: currentPath,
            match,
          };
          currentNode.children.push(currentSegment);
        }
      }

      // Move to the next level
      currentNode = currentSegment;
    }
  });
}

/**
 * Finds the first file path in the tree structure
 * @param {MatchTreeNode[]} nodes - Array of tree nodes to search
 */
export function findFirstFilePath(nodes: MatchTreeNode[]): string | null {
  // eslint-disable-next-line no-restricted-syntax
  for (const node of nodes) {
    if (node.type === 'file') {
      return node.path;
    }
    if (node.type === 'directory' && node.children) {
      const result = findFirstFilePath(node.children);
      if (result) {
        return result;
      }
    }
  }
  return null;
}

/**
 * Takes a list of License Matches and constructs the file tree object.
 */
export default function buildTreeFromMatches(matches: RevisionFileMatchForTree[]): MatchTreeNode[] {
  //  Root starts at "/"
  const root: DirectoryNodeComponent = {
    path: '/',
    children: [],
    type: 'directory',
    name: '/',
  };

  matches.forEach(match => {
    if (match.path === 'DECLARED_LICENSE') {
      return;
    }

    insertPath(root, match);
  });
  //  Recursively collapse all children nodes.
  //  We wrap this in an array because react-arborist expects a list of top-level nodes (in our case just one root).
  return [root];
}
